import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useShallow } from 'zustand/react/shallow';
import { isEqual } from 'lodash';

import i18n from '@nextroll/ar-i18n';
import { BUSINESS_UNIT_ROLLWORKS, Logo } from '@nextroll/ar-style-base';
import { useContainerContext } from '@nextroll/ar-container-context';

import { useRoflStore } from 'store/roflStore';
import useAbortController from 'hooks/useAbortController';

import { RuleValidationResult } from '@nextroll/ar-rollworks-list-builder';

import {
    INodeIssue,
    IWorkflowSchemaTrigger,
    WORKFLOW_NODE,
    WORKFLOW_NODE_VALIDATION_ISSUE_CODES,
    WORKFLOW_TRIGGER_CONTACT_LIST_STATUS,
} from 'types/rofl';

import useRequest from '../../../hooks/useRequest';
import contactsApi from '../../../api/contactsApi';

import { TriggerNodeBase } from '../../basic/NodeBase/NodeBase';
import {
    IContactListOption, TriggerRuleType, TRIGGER_RULE_TYPES,
    ITriggerNodeConfig, ITriggerNodeConfigListMembership, ITriggerNodeConfigCustom,
} from './ContactTriggerNode.type';
import {
    CONTACT_TRIGGER_ERROR,
    CONTACT_TRIGGER_WARNING,
    TRIGGER_RULE_TYPE_OPTIONS
} from './ContactTriggerNode.constant';
import { ListMembershipPanelDropDownSection } from './ContactTriggerNodeSection/ListMembershipPanelDropDownSection';
import { ContactTriggerNodeTypeSection } from './ContactTriggerNodeSection/ContactTriggerNodeTypeSection';
import { TriggerListBuilderSection } from './ContactTriggerNodeSection/TriggerListBuilderSection';

/**
 * TriggerListCriteria is used to display either the selected list or the custom rules in the trigger card.
 * It also displays an error if the list wasn't found.
 */
const TriggerCriteria = ({ loading, config, ruleType }: {
    loading: boolean;
    config?: ITriggerNodeConfig;
    ruleType?: TriggerRuleType;
}) => {
    const contactList = (config as ITriggerNodeConfigListMembership)?.selectedContactList;
    switch (ruleType) {
        case TRIGGER_RULE_TYPES.LIST_MEMBERSHIP:
            return (
                <>
                    <Logo
                        i18n={i18n}
                        businessUnit={BUSINESS_UNIT_ROLLWORKS}
                        wordmark={false}
                        width='16px'
                        disableRollWorksTheme
                    />
                    <span>
                        {i18n.gettext('Contact is member of')}
                        &nbsp;
                        {loading && '?'}
                        {contactList?.name && !loading && <strong>{contactList?.name}</strong>}
                        {!contactList && !loading && (
                            <strong className='text-danger'>{i18n.gettext('Deleted Contact List')}</strong>
                        )}
                    </span>
                </>
            );
        case TRIGGER_RULE_TYPES.CUSTOM:
            return <strong>{i18n.gettext('One or more custom criteria')}</strong>;
        default:
            return null;
    }
};

export const ContactTriggerNode = (props: any) => {
    const { search } = useLocation();

    const preSelectedContactListEid = useMemo(() => {
        const query = new URLSearchParams(search);
        return query.get('contact_list') || undefined;
    }, [search]);

    const { advertisable: { eid: advertisableEid } = {}, user } = useContainerContext();
    const [
        trigger,
        updateTriggerResults,
        updateContentData,
        updateNodeHasError,
    ] = useRoflStore(useShallow(state => [
        state.contentData.trigger,
        state.updateTriggerResults,
        state.updateContentData,
        state.updateNodeHasError,
    ]));
    const [call, loading, requestError] = useRequest();
    const getSignal = useAbortController();

    // local data; 'original' for previous changes vs. 'current' for unsaved changes
    const [triggerConfig, setTriggerConfig] = useState(undefined as {
        original?: ITriggerNodeConfig;
        current?: ITriggerNodeConfig;
    } | undefined);

    const [contactLists, setContactLists] = useState(undefined as IContactListOption[] | undefined);
    const [warning, setWarning] = useState('');
    const [error, setError] = useState('');

    /**
     * method to load the contact list options dropdown data
     */
    const reloadContactLists = useCallback(async () => {
        if (!advertisableEid) {
            return; // not a good time; do nothing
        }

        const abortSignal = getSignal();
        const response: any = await call(() => contactsApi.get('', { advertisable_eid: advertisableEid }, abortSignal));

        if (abortSignal.aborted) {
            return;
        }
        setContactLists(response?.contact_lists || []);
    }, [advertisableEid, call, getSignal]);

    /**
     * load the dropdown option when the node panel is mounted or adv changes
     */
    useEffect(() => {
        reloadContactLists();
    }, [reloadContactLists]);

    /**
     *  check for pre-selected item
     */
    const triggerData = trigger?.data as IWorkflowSchemaTrigger;
    const currentTriggerType = triggerConfig?.current?.ruleType;
    useEffect(() => {
        if (!triggerData?.contact_list_eid) {
            if (currentTriggerType) {
                return; // trigger config has content already; do nothing
            }
            const config = { ruleType: TRIGGER_RULE_TYPES.CUSTOM };
            setTriggerConfig({ original: config, current: config });
            return;
        }
        if (!contactLists) {
            const config = { ruleType: TRIGGER_RULE_TYPES.LIST_MEMBERSHIP };
            setTriggerConfig({ original: config, current: config });
            return;
        }
        const matchedOption = (contactLists || []).find(option => option.eid === triggerData?.contact_list_eid);
        if (!matchedOption) {
            setError(CONTACT_TRIGGER_ERROR.DELETED);
            return;
        }
        const config = {
            ruleType: TRIGGER_RULE_TYPES.LIST_MEMBERSHIP,
            contact_list_eid: matchedOption.eid,
            selectedContactList: matchedOption,
        };
        setTriggerConfig({ original: config, current: config });
        if (matchedOption.status === WORKFLOW_TRIGGER_CONTACT_LIST_STATUS.STOPPED_SYNCING) {
            setWarning(CONTACT_TRIGGER_WARNING.OUT_OF_SYNC);
        }

    // note here we don't track the change of `currentTriggerType` to avoid unnecessary updates
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contactLists, triggerData?.contact_list_eid]);

    const handleUpdateTriggerConfig = useCallback((config?: ITriggerNodeConfig) => {
        if (!config) {
            return; // missing valid config; do nothing
        }
        setTriggerConfig(previousConfig => ({ ...previousConfig, current: config }));
        setWarning(''); // cleanup panel warning message
        setError(''); // cleanup panel error message
        updateNodeHasError(WORKFLOW_NODE.TRIGGER, false); // cleanup node error message
    }, [setTriggerConfig, setWarning, setError, updateNodeHasError]);

    /**
     * update list content if there is an error state
     */
    useEffect(() => {
        if (preSelectedContactListEid && !loading && contactLists) {
            const matchedContactList = contactLists?.find(list => list.eid === preSelectedContactListEid);
            if (matchedContactList
                && matchedContactList.status !== WORKFLOW_TRIGGER_CONTACT_LIST_STATUS.STOPPED_SYNCING) {
                updateContentData(props.id, {
                    ruleType: TRIGGER_RULE_TYPES.LIST_MEMBERSHIP,
                    contact_list_eid: matchedContactList.eid,
                    selectedContactList: matchedContactList,
                });
            }
        }
    }, [loading, contactLists, preSelectedContactListEid, handleUpdateTriggerConfig, updateContentData, props.id]);

    // ruleValidationResult is the validation result of the zeta rule validation endpoint, which is
    // stored in the schema warnings or errors, depending on severity. This is a bit contrived because
    // it bridges the workflow validation system with the pre-existing list rule validation system and needs to be
    // passed to the RollWorksListBuilder component.
    const ruleValidationResult = useMemo(() => {
        const triggerWarnings: INodeIssue[] = props.validationWarnings;
        const triggerErrors: INodeIssue[] = props.validationErrors;
        const ruleNodeIssue = [
            ...(triggerWarnings || []),
            ...(triggerErrors || []),
        ].find(issue => issue.error === WORKFLOW_NODE_VALIDATION_ISSUE_CODES.RULE_VALIDATION_ISSUE
            || issue.warning === WORKFLOW_NODE_VALIDATION_ISSUE_CODES.RULE_VALIDATION_ISSUE
        );
        return ruleNodeIssue && ruleNodeIssue.data as RuleValidationResult;
    }, [props.validationWarnings, props.validationErrors]);

    /**
     * disperse the list builder components to put them piecewisely, only when the feature flag is on
     */
    const {
        banners: TriggerListBanners,
        builder: TriggerListBuilder,
        preview: TriggerListPreview,
        btnPreview: TriggerListBuilderBtnPreview,
        isLoading: isTriggerListBuilderLoading,
        isValid: isTriggerListBuilderValid,
        results: triggerListBuilderResults,
    } = TriggerListBuilderSection({
        advertisableEid,
        user,
        rules: triggerData?.rules,
        onUpdate: (rules, rulesValid) => {
            if (currentTriggerType !== TRIGGER_RULE_TYPES.CUSTOM) {
                return; // custom type not selected; do nothing
            }
            if (isEqual(rules, triggerConfig?.current?.rules)) {
                return; // same rules; do nothing
            }
            handleUpdateTriggerConfig({
                ruleType: TRIGGER_RULE_TYPES.CUSTOM,
                rules: rules || undefined,
                rulesHaveErrors: !rulesValid,
            });
        },
        validationResult: ruleValidationResult,
    });

    /**
     * Here we build the UI elements rendered in the panel.
     *
     * isRequired: determine whether it requires a value before proceeding to the next node.
     * validate: a callback for check if the element has already filled in with a valid value.
     */

    const triggerTypeSectionElement = useMemo(() => {
        const triggerTypeSection = () => (
            <ContactTriggerNodeTypeSection
                data={TRIGGER_RULE_TYPE_OPTIONS}
                error={error}
                onUpdateTriggerNodeType={ruleType => handleUpdateTriggerConfig({ ruleType })}
                triggerNodeType={currentTriggerType}
            />
        );
        return {
            elementConfig: {
                key: 'workload-trigger-node-panel-element-trigger-type-dropdown',
                component: triggerTypeSection,
                isRequired: true,
                validate: () => !!currentTriggerType,
            },
            show: true,
        };
    }, [currentTriggerType, error, handleUpdateTriggerConfig]);

    const currentSelectedContactList = triggerConfig?.current?.ruleType
        === TRIGGER_RULE_TYPES.LIST_MEMBERSHIP && triggerConfig?.current.selectedContactList;
    const listMembershipSectionElement = useMemo(() => {
        const listMembershipSection = () => (
            <ListMembershipPanelDropDownSection
                contactList={currentSelectedContactList || undefined}
                onUpdateContactList={contactList => handleUpdateTriggerConfig({
                    ruleType: TRIGGER_RULE_TYPES.LIST_MEMBERSHIP,
                    contact_list_eid: contactList.eid,
                    selectedContactList: contactList,
                })}
                loading={loading}
                data={contactLists}
                warning={warning}
                error={error}
                onRefresh={reloadContactLists}
            />
        );
        return {
            elementConfig: {
                key: 'workload-trigger-node-panel-element-contact-list-dropdown',
                component: listMembershipSection,
                isRequired: true,
                validate: () => currentTriggerType !== TRIGGER_RULE_TYPES.LIST_MEMBERSHIP
                    || !!currentSelectedContactList,
            },
            show: currentTriggerType === TRIGGER_RULE_TYPES.LIST_MEMBERSHIP
        };
    }, [contactLists, currentSelectedContactList, currentTriggerType,
        error, handleUpdateTriggerConfig, loading, reloadContactLists, warning]);

    const currentTriggerListBuilderRules = triggerConfig?.current?.rules;
    const triggerListBuilderElement = useMemo(() => {
        const triggerListBuilderSection = () => (
            <>
                {TriggerListBanners}
                {TriggerListBuilder}
                {TriggerListPreview}
            </>
        );
        return {
            elementConfig: {
                key: 'workload-trigger-node-panel-element-list-builder',
                component: triggerListBuilderSection,
                isRequired: true,
                validate: () => isTriggerListBuilderValid && !!currentTriggerListBuilderRules // rules need to be valid and not empty
            },
            show: currentTriggerType === TRIGGER_RULE_TYPES.CUSTOM
        };
    }, [
        TriggerListBanners, TriggerListBuilder, TriggerListPreview,
        isTriggerListBuilderValid, currentTriggerListBuilderRules, currentTriggerType,
    ]);

    /**
     * Error handlers here
     *
     * detect error and set the trigger node to the error state
     */
    useEffect(() => {
        if (requestError) {
            updateNodeHasError(WORKFLOW_NODE.TRIGGER, true);
            // display a generic error message instead of exposing the API response
            setError(CONTACT_TRIGGER_ERROR.BASIC);
        }
    }, [requestError, updateNodeHasError]);
    useEffect(() => {
        if (loading || (currentTriggerType !== TRIGGER_RULE_TYPES.CUSTOM)) {
            return; // the rule list builder is not rendered; do nothing
        }

        const previousRulesHaveErrors = (triggerConfig?.original as ITriggerNodeConfigCustom)?.rulesHaveErrors;
        updateNodeHasError(WORKFLOW_NODE.TRIGGER, !!previousRulesHaveErrors);
    }, [loading, currentTriggerType, triggerConfig?.original, updateNodeHasError]);

    /**
     * build the details panel content by iterating through all possible elements
     */
    const panelElementConfigs = useMemo(() => {
        const configs = [
            triggerTypeSectionElement,
            listMembershipSectionElement,
            triggerListBuilderElement,
        ];
        return configs.filter(elementConfigItem => elementConfigItem.show).map(
            elementConfigItem => elementConfigItem.elementConfig);
    }, [listMembershipSectionElement, triggerTypeSectionElement, triggerListBuilderElement]);

    /**
     * determine if it is necessary to insert a "preview" button between "cancel" and "apply"
     */
    const actionButtons = useMemo(() => {
        if (currentTriggerType !== TRIGGER_RULE_TYPES.CUSTOM || !TriggerListBuilderBtnPreview) {
            return [];
        }
        // need to add a unique key to silence build warnings
        return React.cloneElement(TriggerListBuilderBtnPreview, { key: 'workflow-node-panel-btn-preview' });
    }, [currentTriggerType, TriggerListBuilderBtnPreview]);

    /**
     * total number of contacts used in the trigger node content
     */
    const totalContacts = useMemo(() => {
        if (triggerConfig?.original?.ruleType === TRIGGER_RULE_TYPES.LIST_MEMBERSHIP) {
            return triggerConfig?.original?.selectedContactList?.size;
        }
        triggerListBuilderResults && updateTriggerResults(triggerListBuilderResults); // keep a copy of the builder results in the store
        return triggerListBuilderResults?.totalCount;
    }, [triggerConfig, triggerListBuilderResults, updateTriggerResults]);

    return (
        <div className='workflow-trigger'>
            <TriggerNodeBase<ITriggerNodeConfig>
                {...props}
                panelSubtitle={i18n.gettext('Trigger this workflow when Contacts match these criteria:')}
                panelElementConfigs={panelElementConfigs}
                loading={loading || isTriggerListBuilderLoading}
                content={triggerConfig}
                setContent={setTriggerConfig}
                warning={props.warning}
                error={error || props.error}
                actionButtons={actionButtons}
            >
                <p>{i18n.gettext('When contacts meet the following criteria:')}</p>
                <div className='workflow-contact-trigger-node-title-container'>
                    <TriggerCriteria
                        config={triggerConfig?.original}
                        loading={loading}
                        ruleType={triggerConfig?.original?.ruleType}
                    />
                </div>
                <a type='button'>{i18n.gettext('Edit')}</a>
                {totalContacts !== undefined && (
                    <div className='small workflow-contact-trigger-node-num-of-contacts'>
                        {i18n.ngettext(
                            '<TOTAL /> contact meet the trigger criteria at this time.',
                            '<TOTAL /> contacts meet the trigger criteria at this time.',
                            totalContacts || 0,
                            { TOTAL: <strong>{i18n.numeral(totalContacts).format('0,0')}</strong> },
                        )}
                    </div>
                )}
            </TriggerNodeBase>
        </div>
    );
};
